import React from "react";
import { Chip, CircularProgress, Link, Stack, Table, Typography } from "@mui/joy";
import { useQuery } from "react-query";
import { ApiMessage, ApiMessageTypes } from "../../shared/interfaces";
import FileViewer, { FileViewerArgs } from "../fileviewer/FileViewer";
import { OpenInNewOutlined } from "@mui/icons-material";
import { timeDifference } from "../../util/dataUtil";
import Message from "../Message";
import { PrimeQueryKeys } from "../../shared/constants";
import { AdminApi } from "../../api/admin";
import { setPageTitle } from "../../util/elementUtil";

function qualityToColor(quality: string): any {
    if (quality === "not-good") {
        return "danger";
    }

    if (quality === "great") {
        return "success";
    }

    return "warning";
}

function getDatadogLink(msg: ApiMessage): string {
    const millisPerDay = 24 * 60 * 60 * 1000;
    return (
        `https://us3.datadoghq.com/logs?query=%40resultDescription.requestId%3A${msg.requestId}&agg_m=count` +
        `&agg_m_source=base&agg_t=count&cols=host%2Cservice&fromUser=true&messageDisplay=inline` +
        `&refresh_mode=sliding&storage=hot&stream_sort=desc&viz=stream` +
        `&from_ts=${new Date(msg.createdAt).getTime() - millisPerDay}` +
        `&to_ts=${new Date(msg.createdAt).getTime() + millisPerDay}&live=true`
    );
}

const emojis = [
    "💩",
    "👽",
    "🤖",
    "🦄",
    "👹",
    "👺",
    "💀",
    "👻",
    "👾",
    "🎃",
    "😺",
    "😈",
    "💥",
    "🌪️",
    "🧙‍♂️",
    "🧞‍♀️",
    "🧛‍♂️",
    "🧟‍♀️",
    "👑",
    "🧶",
    "🐲",
    "🐉",
    "🎩",
    "🍕",
    "🍭",
    "🍿",
    "🍔",
    "🍣",
    "🍦",
    "🍪",
    "🍩",
    "🍕",
    "🌈",
    "🎉",
    "🎈",
    "🎂",
    "🎩",
    "🎸",
    "🕶️",
    "🦋",
    "🐒",
    "🦔",
    "🐸",
    "🦉",
    "🐙",
    "🐌",
    "🦖",
    "🦧",
    "🦎",
    "🦍",
    "🍄",
    "🪲",
    "🍌",
    "🪐",
    "🔥",
    "💫",
    "🌈",
    "🌞",
    "🌜",
    "🌵",
    "🎱",
    "🧨",
    "🚀",
    "🛸",
    "🏴‍☠️",
    "🪅",
    "🔮",
    "🧸",
    "🧁",
    "🥳",
];

/**
 * Get an emoji for the conversation
 * @param conversationId
 */
function getEmojiForConversation(conversationId: string) {
    let hash = 0;
    for (let i = 0; i < conversationId.length; i++) {
        hash = conversationId.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % emojis.length;
    return emojis[index];
}

const styles = {
    loadingIndicatorContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100vw",
        height: "100vh",
    },
    tableContainerStack: {
        display: "flex",
        flex: 1,
        width: "100%",
        minHeight: 0,
        minWidth: "50%",
        px: 4,
        overflowY: "scroll",
        flexShrink: 1,
        flexGrow: 1,
    },
    contentContainer: {
        flex: "1 1 auto",
        position: "relative",
        overflowY: "auto",
        display: "flex",
    },
};

export default function AdminOverview() {
    setPageTitle("Admin");

    const { data: messages, status } = useQuery(PrimeQueryKeys.adminOverview, AdminApi.getOverview);

    const [currentFileViewerFile, setCurrentFileViewerFile] = React.useState<FileViewerArgs | null>(
        null,
    );

    // Stats
    const toPercentage = (a: number | undefined, b: number | undefined) =>
        `${((100.0 * (a || 0)) / (b || 1)).toFixed(1)}%`;

    const userCount = new Set(messages?.map((m) => m.user.id)).size;

    const pctError = toPercentage(
        messages?.filter((m) => m.response?.messageType === ApiMessageTypes.Error).length,
        messages?.length,
    );

    const questionsWithFeedback = messages?.filter((m) => m.response?.quality).length || 0;
    const pctQuestionsWithFeedback = toPercentage(questionsWithFeedback, messages?.length);
    const pctWithRating = (rating: string) =>
        toPercentage(
            messages?.filter((m) => m.response?.quality === rating).length,
            questionsWithFeedback,
        );

    const getChipWithLabel = (label: string, val: any) => {
        return (
            <>
                <Typography color="neutral" level="body-xs">
                    {label}: &nbsp;
                    <Typography level="title-sm">{val}</Typography>
                </Typography>
            </>
        );
    };

    const initialLoadInProgress = status === "loading";

    return (
        <Stack spacing={2} margin={0}>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 4, paddingTop: 4 }}>
                <Typography level="h3">Admin overview</Typography>
                {!initialLoadInProgress && (
                    <Stack direction="row" spacing={1}>
                        {getChipWithLabel("Users", userCount)}
                        {getChipWithLabel("Error", pctError)}
                        {getChipWithLabel("W/feedback", pctQuestionsWithFeedback)}
                        {getChipWithLabel("not-good", pctWithRating("not-good"))}
                        {getChipWithLabel("ok", pctWithRating("okay"))}
                        {getChipWithLabel("great", pctWithRating("great"))}
                    </Stack>
                )}
            </Stack>

            {initialLoadInProgress && (
                <Stack spacing={2} sx={styles.loadingIndicatorContainer}>
                    <CircularProgress color="neutral" size="lg" />
                    <Typography>Loading all the messages and stuff. Are you excited?</Typography>
                </Stack>
            )}

            {!initialLoadInProgress && (
                <Stack direction="row" sx={styles.contentContainer}>
                    <Stack sx={styles.tableContainerStack}>
                        <Table variant="outlined">
                            <thead>
                                <tr>
                                    <th style={{ width: "35%" }}>Query</th>
                                    <th>Response</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(messages || []).map((row) => (
                                    <tr key={row.id}>
                                        <td style={{ verticalAlign: "top" }}>
                                            <Stack direction="column" spacing={2}>
                                                <Typography color="neutral" level="body-xs">
                                                    <Typography level="body-xs">
                                                        Request ID:{" "}
                                                    </Typography>
                                                    <Link
                                                        color="neutral"
                                                        href={getDatadogLink(row)}
                                                        target="_blank"
                                                        endDecorator={<OpenInNewOutlined />}
                                                    >
                                                        {row.requestId}
                                                    </Link>
                                                    <br />
                                                    <Typography level="body-xs">
                                                        Conversation ID:{" "}
                                                    </Typography>
                                                    {getEmojiForConversation(row.conversationId)}{" "}
                                                    {row.conversationId}
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <Typography level="title-sm">
                                                        {row.user.name}
                                                    </Typography>
                                                    <Typography color="neutral" level="body-xs">
                                                        {new Date(row.createdAt).toLocaleString()}
                                                    </Typography>
                                                </Stack>

                                                <Message
                                                    message={row}
                                                    currentFileViewerFile={currentFileViewerFile}
                                                    setCurrentFileViewerFile={
                                                        setCurrentFileViewerFile
                                                    }
                                                    singleMessageView={true}
                                                />
                                            </Stack>
                                        </td>
                                        <td style={{ verticalAlign: "top" }}>
                                            {!row.response && (
                                                <Typography fontFamily="monospace" level="body-xs">
                                                    Marvin did not provide a response.&nbsp;
                                                    <Link
                                                        color="neutral"
                                                        href={getDatadogLink(row)}
                                                        target="_blank"
                                                        endDecorator={<OpenInNewOutlined />}
                                                    >
                                                        Check the logs.
                                                    </Link>
                                                </Typography>
                                            )}

                                            {row.response && (
                                                <Stack direction="column" spacing={2}>
                                                    <Stack direction="row" spacing={1}>
                                                        {row.response.messageType ===
                                                            ApiMessageTypes.Error && (
                                                            <Chip color="danger">Error</Chip>
                                                        )}

                                                        {getChipWithLabel(
                                                            "Response time",
                                                            timeDifference(
                                                                row.createdAt,
                                                                row.response.createdAt,
                                                            ),
                                                        )}

                                                        {row.response.quality && (
                                                            <Chip
                                                                color={qualityToColor(
                                                                    row.response.quality,
                                                                )}
                                                            >
                                                                <Typography>
                                                                    <Typography level="body-xs">
                                                                        Quality:{" "}
                                                                    </Typography>
                                                                    <Typography level="title-sm">
                                                                        {row.response.quality}
                                                                    </Typography>
                                                                </Typography>
                                                            </Chip>
                                                        )}
                                                    </Stack>

                                                    {row.response.feedback && (
                                                        <Typography>
                                                            <Typography level="title-sm">
                                                                Feedback:{" "}
                                                            </Typography>
                                                            <Typography>
                                                                {row.response.feedback}
                                                            </Typography>
                                                        </Typography>
                                                    )}

                                                    <Message
                                                        key={row.id}
                                                        message={row.response}
                                                        currentFileViewerFile={
                                                            currentFileViewerFile
                                                        }
                                                        setCurrentFileViewerFile={
                                                            setCurrentFileViewerFile
                                                        }
                                                        singleMessageView={true}
                                                    />
                                                </Stack>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Stack>
                    <Stack
                        spacing={0}
                        sx={{
                            display: currentFileViewerFile ? "flex" : "none",
                            flex: "1 1 auto",
                            px: 2,
                        }}
                    >
                        {currentFileViewerFile && (
                            <FileViewer
                                fileArgs={currentFileViewerFile}
                                showTitleAndClose={true}
                                onClose={() => setCurrentFileViewerFile(null)}
                            />
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
